import CTNButton from '@ctn/ui/components/ctn-button';
import CTNButtonsGroup from '@ctn/ui/components/ctn-buttons-group';
import classNames from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';

import { useGlobalContext } from '@/context/global.context';
import { useSolicitudContext } from '@/context/solicitud.context';

import { mapRepresentanteDBToForm, mapRepresentanteFormToDB, mapSujetoFormToDB, updateGlobalWithSujeto } from '@/helpers/formSujeto';
import { type IRepresentante } from '@/interfaces/model/don.interface';
import { findID } from '@/utils/id';

import RepresentanteListado from '../RepresentanteListado';
import { defaultValuesRepresentante, resolverRepresentante, type RepresentanteForm, type SujetoForm } from './config';
import DatosPersona from './form/DatosPersona';
import Direccion from './form/Direccion';
import DocumentoPoder from './form/DocumentoPoder';

type TabRepresentanteProps = {
	readonly setDisableActions: (value: boolean) => void;
	readonly isDisabled?: boolean;
	readonly className?: string;
};

const TabRepresentante = ({ className, setDisableActions, isDisabled }: TabRepresentanteProps) => {
	const {
		maestrosTramite: { tipoDocumentoIdent, tipoIntervencion, tipoPersona, configuracionTramite },
		maestrosGeneric: { tipoDireccion },
	} = useGlobalContext();

	/**
	 *  === Datos globales de la solicitud ===
	 */
	const { getFormValues } = useSolicitudContext();
	const globalValues = getFormValues();

	/**
	 *  === Formulario temporal de la modal interviniente ===
	 */
	const sujetoForm = useFormContext<SujetoForm>();
	const { update, append } = useFieldArray({
		control: sujetoForm.control,
		name: 'representantes',
	});

	const valuesSujeto = sujetoForm.watch();

	const { representantes = [] } = valuesSujeto;

	const currentGlobalValues = useMemo(() => {
		const currentSujeto = mapSujetoFormToDB(valuesSujeto, {
			tipoDocumentoIdent,
			tipoDireccion,
			tipoIntervencion,
			tipoPersona,
		});

		return updateGlobalWithSujeto(currentSujeto, globalValues, tipoIntervencion);
	}, [valuesSujeto, globalValues, tipoIntervencion, tipoPersona, tipoDocumentoIdent, tipoDireccion]);

	/**
	 * === Formulario temporal de los datos del representante ===
	 * Al guardarlos se añadirán al formulario de interviniente
	 */
	const methods = useForm<RepresentanteForm>({
		defaultValues: defaultValuesRepresentante(),
		mode: 'onChange',
		resolver: resolverRepresentante({
			maxSizeAllDoc: configuracionTramite?.pesoMaximoSolicitud ?? 0,
			globalValues: currentGlobalValues,
		}),
	});
	const { handleSubmit, reset, watch } = methods;

	const isNewRepresentante = !watch('id') && !watch('idReact');

	const [openRepresentante, setOpenRepresentante] = useState(false);

	const onEdit = (data: IRepresentante) => {
		const dataMapped = mapRepresentanteDBToForm(data);
		reset(dataMapped);
		setOpenRepresentante(true);
	};

	const onClose = () => {
		setOpenRepresentante(false);
	};

	const showForm = () => {
		reset(defaultValuesRepresentante());
		setOpenRepresentante(true);
	};

	const onSave = (value: IRepresentante) => {
		const index = findID({ list: representantes, idReact: value.idReact, id: value.id });
		if (index === -1) {
			append(value);
		} else {
			update(index, value);
		}
	};

	const onSubmit = async (data: RepresentanteForm) => {
		const dataMapped = mapRepresentanteFormToDB(data, { tipoDocumentoIdent, tipoDireccion });

		onSave(dataMapped);
		onClose();
	};

	useEffect(() => {
		setDisableActions(openRepresentante);

		return () => {
			setDisableActions(false);
		};
	}, [openRepresentante]);

	return (
		<div className={classNames('u-mt-24', className)}>
			<div className="o-splitter o-splitter--responsive">
				<h4 className="c-pnp-heading-h4">Representantes</h4>
				{!openRepresentante && !isDisabled && (
					<CTNButton
						mode="secondary"
						label="Añadir representante"
						minWidth="140"
						size="size-40"
						onClick={showForm}
					/>
				)}
			</div>
			<RepresentanteListado className="u-mt-24 u-mt-16@portrait" isDisplay={isDisabled} onEdit={onEdit} />
			{openRepresentante && (
				<fieldset className="[ c-pnp-form-group c-pnp-form-group--accent  ] [ u-mt-24 ]">
					<FormProvider {...methods}>
						<form>
							<DatosPersona title="Datos del representante" />
							<DocumentoPoder title="Documento de poder de representación" className="u-mt-16" />
							<Direccion title="Dirección del representante" className="u-mt-16" />
							<CTNButtonsGroup align="right">
								<CTNButton mode="tertiary" label="Cancelar" size="size-40" onClick={onClose} />
								<CTNButton
									mode="secondary"
									label={isNewRepresentante ? 'Añadir representante' : 'Actualizar representante'}
									minWidth="140"
									size="size-40"
									onClick={handleSubmit(onSubmit)}
								/>
							</CTNButtonsGroup>
						</form>
					</FormProvider>
				</fieldset>
			)}
		</div>
	);
};

export default TabRepresentante;
